import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputType } from '@vinnova/ui';
import { merge, Observable, Subject, Subscription, take } from 'rxjs';
import { FormQuestionStandard } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import { QuestionStructureTable } from '../../../core/models/question/question-table.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';

@UntilDestroy()
@Component({
  selector: 'lib-question-input-table',
  templateUrl: './question-input-table.component.html',
  styleUrls: ['./question-input-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionInputTableComponent implements OnInit, OnChanges {
  value: FormControl<string>;

  @Input() questionsInRow: number;
  @Input() labelPrefix: string;
  @Input() repeaterGroupId: string;

  InputType = InputType;
  inputType: InputType;
  questionNumber$: Observable<unknown>;
  readOnly$: Observable<ReadonlyState>;
  markTableAsTouched = false;
  titles: {
    columns: { id: string; title: string }[];
    rows: { id: string; title: string }[];
  };
  questionChange = new Subject<void>();
  questionChange$ = this.questionChange.asObservable();
  activeLang$: Observable<string>;
  checkValidation$: Observable<boolean>;
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  showChanges = input<boolean>();
  readonly previousAnswersService = inject(PreviousAnswersService);

  ReadonlyState = ReadonlyState;

  _questionForm: FormGroup<ControlsOf<FormQuestionStandard>>;
  @Input()
  set questionForm(form: FormGroup<ControlsOf<FormQuestionStandard>>) {
    this._questionForm = form;
    this.value = this._questionForm.get('value');
    this.markTableAsTouched = form.touched;
    this.value.touch$.pipe(take(1)).subscribe((value) => {
      this.markTableAsTouched = value;
      this.cdr.markForCheck();
    });
  }
  get questionForm() {
    return this._questionForm;
  }

  _question: Question;
  @Input() set question(value: Question) {
    this._question = value;
    this.questionChange.next();
    this.inputType = this.question.validation.isNumber
      ? InputType.Number
      : (this.question.structure as QuestionStructureTable).isAmount
        ? InputType.Amount
        : InputType.Text;
  }
  get question() {
    return this._question;
  }

  get rows() {
    return (this.question.structure as QuestionStructureTable).rows;
  }

  get columns() {
    return (this.question.structure as QuestionStructureTable).columns;
  }

  get sumRows() {
    return (this.question.structure as QuestionStructureTable).sumRows;
  }

  get sumColumns() {
    return (this.question.structure as QuestionStructureTable).sumColumns;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private questionNumberService: QuestionNumberService,
    private translocoService: TranslocoService,
    private translatePipe: TranslatePipe,
    private formsQuery: RendererFormsQuery,
    private formValidationService: FormValidationService,
  ) {}

  ngOnInit() {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;

    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );

    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );

    merge(this.translocoService.langChanges$, this.questionChange$)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const structure = this.question.structure as QuestionStructureTable;
        this.titles = {
          columns: structure.columns.map((c) => {
            return {
              id: c.id,
              title: this.translatePipe.transform(
                this.question.translations?.list[c.id],
                this.translocoService.getActiveLang(),
                c.title,
              ),
            };
          }),
          rows: structure.rows.map((r) => {
            return {
              id: r.id,
              title: this.translatePipe.transform(
                this.question.translations?.list[r.id],
                this.translocoService.getActiveLang(),
                r.title,
              ),
            };
          }),
        };
        this.cdr.markForCheck();
      });

    this.checkValidation$ = this.formValidationService.showErrors$;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe();
    }
  }

  onTouched() {
    this.value.markAsTouched();
  }
}
